import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { findNamedMatches } from "./fuzzy";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type FilterRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

type ResultRow = FilterRow;

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
// (delete_show removes one show; backup_restore + import_file REPLACE all data.)
const destructive = (a: ChatAction) =>
  a.kind === "delete_show" || a.kind === "backup_restore" || a.kind === "import_file" ||
        a.kind === "library_remove" || a.kind === "library_merge";
const warnFor = (action: ChatAction): string => {
  if (action.kind === "library_merge") return `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`;
  if (action.kind === "library_remove") return `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`;
  if (action.kind === "import_file") return "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel.";
  if (action.kind === "backup_restore") return `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`;
  const label = action.kind === "delete_show" ? action.label ?? "this show" : "this show";
  return `Delete ${label}? Tap Confirm, or Cancel.`;
};
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
type PendingHold = { action: ChatAction; rest: ChatAction[] };

const STORE_KEY = "oriora.oriora_encore.chat.v1";
const GREETING: ChatTurn = {
  role: "assistant",
  content: "Tell me what to do — I'll log a show, find one, rate it, or recap your live-music year. No clicking.",
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (Array.isArray(p) && p.length) return p;
    }
  } catch {
    /* ignore */
  }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100)));
  } catch {
    /* ignore */
  }
}

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a show. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  // `save_to_cloud`'s "conversation" option needs the chat text — passed to the executor as ctx.
  const transcriptOf = (t: ChatTurn[]): string => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  async function runQueue(queue: ChatAction[], ctx?: { transcript?: string }): Promise<{ lines: string[]; rows?: ResultRow[]; changed: boolean; hold: PendingHold | null }> {
    const lines: string[] = [];
    let rows: ResultRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, ctx);
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  // A photo turn is filed ON-DEVICE onto one of the user's logged shows (a memory/attachment): no
  // model call, no cost. The show is named in the same message ("photo + the national"); if it's
  // ambiguous we ask which one, keeping the photo attached so the next message can name it.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const shows = await store.listShows({ limit: 50000 });
      if (shows.length === 0) {
        setTurns([...next, { role: "assistant", content: "Log a show first, then I'll attach the photo to it." }]);
        return; // keep the photo attached
      }
      const named = shows.map((s) => ({ ...s, name: [s.artists.join(" "), s.venue, s.city].filter(Boolean).join(" ") }));
      const label = (s: (typeof shows)[number]) => s.venue ? `${s.artists.join(", ")} @ ${s.venue}` : s.artists.join(", ");
      const q = text.trim();
      const hits = q ? findNamedMatches(named, q) : [];
      let target: (typeof named)[number] | null = hits.length === 1 ? hits[0] : null;
      if (q && hits.length > 1) {
        setTurns([...next, { role: "assistant", content: `Which show is this photo for?\n${hits.slice(0, 6).map(label).join("\n")}` }]);
        return; // keep the photo attached
      }
      if (!target && shows.length === 1) target = named[0]; // only one show — no need to ask
      if (!target) {
        setTurns([...next, { role: "assistant", content: `Which show is this photo for? Name the artist or venue.\n${named.slice(0, 6).map(label).join("\n")}` }]);
        return; // keep the photo attached so the next message can name it
      }
      await store.setShowPhoto(target.id, photo!);
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Attached your photo to ${label(target)}.` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, { transcript: transcriptOf(next) });
      if (r.changed) onChanged?.();
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", rows: r.rows }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      let rows: ResultRow[] | undefined;
      const result = await executeAction(action);
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      const r = await runQueue(rest, { transcript: transcriptOf(turns) });
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }
  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : "") }]);
  }

  return (
    <div className="oen-chat">
      <div className="oen-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`oen-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`oen-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && (
          <div className="oen-msgrow">
            <div className="oen-bubble bot thinking">thinking</div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="oen-composer oen-confirm">
          <button
            className="btn btn-primary"
            style={{ background: "var(--rose, #d14a4a)", borderColor: "var(--rose, #d14a4a)" }}
            onClick={confirmPending}
            disabled={busy}
          >
            {pending.action.kind === "delete_show" ? "Confirm delete" : "Confirm"}
          </button>
          <button className="btn" onClick={cancelPending} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="oen-composer">
            <textarea
              value={input}
              placeholder={photo ? "Name the show, or just send…" : "Tell me what to do…"}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            <button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="oen-rows">
      {rows.map((r) => (
        <div key={r.id} className="oen-rowitem">
          <span>
            {r.artists}
            {r.rating > 0 ? ` · ${"★".repeat(r.rating)}` : ""}
          </span>
          <span className="tnum">{[r.venue, r.city].filter(Boolean).join(", ") || r.seen_on}</span>
        </div>
      ))}
    </div>
  );
}
